import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerRoleAPI} from "@/api/player_role";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	AREA_OPTIONS,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";

const HandleSettingsPlayerRolePage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({active: true});

	const getPlayerRole = async () => {
		setLoading(true);
		try {
			const response = await PlayerRoleAPI.getById(id);
			setFields({
				...response,
				currentDepartment: response.department
					? {value: response.department, label: response.department}
					: null,
				area: response.area
					? AREA_OPTIONS.find((o) => o.value === response.area) || null
					: null,
			});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getPlayerRole();
		}
	}, [edit]);

	const getRequiredFields = () => ["name", "area"];

	const saveRole = async () => {
		const requiredErrors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(requiredErrors);

		if (!requiredErrors.length) {
			setLoading(true);
			const body = {
				...fields,
				area: fields.area?.value || null,
				code: fields.code?.trim().toUpperCase() || null,
				department: fields.currentDepartment?.value || null,
				active: fields.active !== false,
			};

			try {
				const response = edit
					? await PlayerRoleAPI.update(id, body)
					: await PlayerRoleAPI.create(body);
				setTimeout(() => {
					navigate(
						edit
							? `/settings/player-roles/${response?.id || id}`
							: "/settings/player-roles",
					);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveRole();
	};

	const goBack = () => {
		if (edit) {
			navigate("/settings/player-roles/" + id);
		} else {
			navigate("/settings/player-roles");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: !!checkRequiredFields(getRequiredFields(), fields).length,
		},
	];

	const breadcrumbsElements = [
		{label: "Ruoli giocatori", path: "/settings/player-roles"},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields ? fields.name : "",
				path: "/settings/player-roles/" + id,
			},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo ruolo"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "code",
										label: "Codice (massimo 4 caratteri)",
									})}
								</div>
								<div className="col-12 col-md-4">
									<VLFSelectNew
										label="Reparto"
										sorted={false}
										options={[
											{value: "P", label: "Portieri"},
											{value: "D", label: "Difensori"},
											{value: "C", label: "Centrocampisti"},
											{value: "A", label: "Attaccanti"},
										]}
										value={fields.currentDepartment || null}
										onChange={(value) =>
											setFields({...fields, currentDepartment: value})
										}
									/>
								</div>
								<div className="col-12 col-md-4">
									<VLFSelectNew
										label="Disponibilità nelle scelte"
										sorted={false}
										isClearable={false}
										options={[
											{value: true, label: "Attivo"},
											{value: false, label: "Inattivo"},
										]}
										value={{
											value: fields.active !== false,
											label: fields.active === false ? "Inattivo" : "Attivo",
										}}
										onChange={(value) =>
											setFields({...fields, active: value.value})
										}
									/>
								</div>

								<div className="col-sm-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "name",
										label: "Nome",
										required: true,
									})}
								</div>
								<div className="col-sm-12 col-md-4">
									<VLFSelectNew
										options={AREA_OPTIONS}
										value={fields.area || null}
										onChange={(value) => setFields({...fields, area: value})}
										label="Ruolo principale"
										sorted={false}
									/>
								</div>
								<div className="col-sm-12 col-md-4">
									{getTextField({
										...defaultTextField,
										fieldKey: "sorting",
										label: "Ordinamento",
										type: "number",
									})}
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
			</div>
		</Page>
	);
};

export default HandleSettingsPlayerRolePage;
